import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import authService from '../../services/authService';
import './Auth.css';

// Regex pattern for special characters
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    token: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get email and token from URL parameters
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    
    if (!email || !token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      email: email,
      token: token
    }));
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password requirements (base app requirements)
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Check for required character types
    const hasUppercase = /[A-Z]/.test(formData.newPassword);
    const hasLowercase = /[a-z]/.test(formData.newPassword);
    const hasDigit = /\d/.test(formData.newPassword);
    const hasSpecialChar = SPECIAL_CHARS_REGEX.test(formData.newPassword);

    if (!hasUppercase) {
      setError('Password must contain at least one uppercase letter');
      setLoading(false);
      return;
    }

    if (!hasLowercase) {
      setError('Password must contain at least one lowercase letter');
      setLoading(false);
      return;
    }

    if (!hasDigit) {
      setError('Password must contain at least one number');
      setLoading(false);
      return;
    }

    if (!hasSpecialChar) {
      setError('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword({
        email: formData.email,
        token: formData.token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Password Reset Successful</h1>
            <p>Your password has been updated</p>
          </div>
          
          <div className="success-message">
            <p>
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
          </div>

          <div className="auth-footer">
            <Link to="/login" className="auth-button">
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error && !formData.email) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Reset Link</h1>
          </div>
          
          <div className="error-message">
            <p>{error}</p>
          </div>

          <div className="auth-footer">
            <p>
              <Link to="/forgot-password" className="auth-link">
                Request a new reset link
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Your Password</h1>
          <p>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              disabled
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your new password"
              minLength="8"
            />
            <div style={{ marginTop: '8px' }}>
              <small style={{color: '#666', fontSize: '0.85em', display: 'block', marginBottom: '4px'}}>
                Must be at least 8 characters with uppercase, lowercase, number, and special character
              </small>
              <small style={{color: '#f59e0b', fontSize: '0.85em', fontStyle: 'italic'}}>
                ⚠️ Teams and Organizations may have more restrictive password requirements.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Confirm your new password"
              minLength="8"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;